import React, { Fragment, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import { DialogContent, DialogActions, DialogTitle } from '@mui/material';
import { withStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useLangFile } from 'js/context/LanguageContext';
import Chip from '@mui/material/Chip';

import { WarningRounded, Info } from '@mui/icons-material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import { usePrescriptionJob } from 'js/components/Prescription/PrescriptionJobContext';
import {
  calculatePrescriptionLimitAdjustedIntervals,
  getBulkPrescriptionIntervalArea,
} from 'js/components/Prescription/PrescriptionUtils';
import useFirebaseAnalytics, { FIREBASE_EVENTS } from '../../../../hooks/useFirebaseAnalytics';
import { PRESCRIPTION_UNIT } from '../../../../constants/PrescriptionConstants';

const styles = (theme) => ({
  title: {},
  adjustWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: theme.spacing(2),
    flexDirection: 'column',
  },
  divider: {
    margin: [[theme.spacing(2), 0]],
  },
  chip: {
    margin: theme.spacing(1),
    position: 'absolute',
    top: 0,
    right: 0,
  },
});

const PrescriptionAutoAdjustDialog = ({
  isBulkPrescription,
  appliedInitialIntervals,
  updateBulkJobsIntervals,
  bulkPrescriptionJobs,
  classes,
  open,
  onSetOpen,
}) => {
  const analytics = useFirebaseAnalytics();
  const LangFile = useLangFile();

  const { prescriptionJob, setPrescriptionJob } = usePrescriptionJob();
  const { unit, maxPrescription, totalPrescription } = prescriptionJob;

  const isBulkMode = isBulkPrescription && !appliedInitialIntervals;

  // Accumulated values across bulk jobs
  const accumulatedMaxPrescription = isBulkMode
    ? Object.values(bulkPrescriptionJobs || {}).reduce(
        (sum, job) => sum + (job.maxPrescription || 0),
        0
      )
    : maxPrescription;

  const accumulatedTotalPrescription = isBulkMode
    ? Object.values(bulkPrescriptionJobs || {}).reduce(
        (sum, job) => sum + (job.totalPrescription || 0),
        0
      )
    : totalPrescription;

  let unitString;
  let limit = Number(accumulatedMaxPrescription).toFixed(2);
  let total = Number(accumulatedTotalPrescription).toFixed(2);
  switch (unit) {
    case PRESCRIPTION_UNIT.LITER:
      unitString = LangFile.PrescriptionSettings.unit.liters.short;
      break;
    case PRESCRIPTION_UNIT.PIECES_M2:
      unitString = `t.${LangFile.PrescriptionSettings.unit.pieces.short}`;
      limit = Number(accumulatedMaxPrescription / 1000).toFixed();
      total = Number(accumulatedTotalPrescription / 1000).toFixed();
      break;
    default:
      unitString = LangFile.PrescriptionSettings.unit.kilogram.short;
      break;
  }

  const enableAdjust = Math.abs(Number(limit - total)) > 0;

  const overLimit = total > limit;
  const underLimit = total < limit;

  const handleOnClose = useCallback(() => {
    onSetOpen(false);
    analytics.logEvent(FIREBASE_EVENTS.VRM_EDITOR_AUTO_ADJUST_CLOSE);
  }, []);

  const handleOnAdjust = useCallback(() => {
    let adjustedIntervals;

    if (isBulkMode && bulkPrescriptionJobs) {
      // Compute total areaFinal per interval across all jobs
      const areaFinals = prescriptionJob.intervals.map((interval) =>
        getBulkPrescriptionIntervalArea(interval, bulkPrescriptionJobs)
      );

      const bulkOverrides = {
        totalPrescription: accumulatedTotalPrescription,
        maxPrescription: accumulatedMaxPrescription,
        areaFinals,
      };

      ({ adjustedIntervals } = calculatePrescriptionLimitAdjustedIntervals(
        prescriptionJob,
        bulkOverrides
      ));
    } else {
      ({ adjustedIntervals } = calculatePrescriptionLimitAdjustedIntervals(prescriptionJob));
    }

    const changes = {
      intervals: adjustedIntervals,
    };

    setPrescriptionJob(changes, true);

    if (isBulkMode) {
      updateBulkJobsIntervals(adjustedIntervals);
    }

    analytics.logEvent(FIREBASE_EVENTS.VRM_EDITOR_AUTO_ADJUST_SUBMIT);
  }, [
    prescriptionJob,
    isBulkMode,
    bulkPrescriptionJobs,
    accumulatedTotalPrescription,
    accumulatedMaxPrescription,
    updateBulkJobsIntervals,
  ]);

  useEffect(() => {
    if (open) {
      analytics.logEvent(FIREBASE_EVENTS.VRM_EDITOR_AUTO_ADJUST_INIT);
    }
  }, [open]);

  return (
    <Fragment>
      <Dialog open={open} fullWidth maxWidth={'md'}>
        <DialogTitle className={classes.title}>
          {LangFile.PrescriptionAutoAdjustDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>{LangFile.PrescriptionAutoAdjustDialog.info1}</Typography>
          <Typography gutterBottom>{LangFile.PrescriptionAutoAdjustDialog.info2}</Typography>
          <Typography gutterBottom>{LangFile.PrescriptionAutoAdjustDialog.info3}</Typography>

          <Divider className={classes.divider} />

          <div className={classes.adjustWrapper}>
            <Typography gutterBottom>
              {LangFile.PrescriptionAutoAdjustDialog.limit}{' '}
              <b>
                {limit} {unitString}
              </b>
            </Typography>

            <Typography gutterBottom>
              {LangFile.PrescriptionAutoAdjustDialog.total}{' '}
              <b>
                {total} {unitString}
              </b>
            </Typography>

            <Box
              sx={{
                mt: 2,
              }}
            >
              <Button
                onClick={handleOnAdjust}
                color="primary"
                variant="contained"
                disabled={!enableAdjust}
              >
                {LangFile.PrescriptionAutoAdjustDialog.adjust}
              </Button>
            </Box>

            {overLimit && (
              <Chip
                avatar={
                  <Avatar>
                    <WarningRounded />
                  </Avatar>
                }
                className={classes.chip}
                label={LangFile.PrescriptionAutoAdjustDialog.overLimit}
                color="secondary"
              />
            )}

            {underLimit && (
              <Chip
                avatar={
                  <Avatar>
                    <Info />
                  </Avatar>
                }
                className={classes.chip}
                label={LangFile.PrescriptionAutoAdjustDialog.underLimit}
                color="primary"
              />
            )}
          </div>

          <Divider className={classes.divider} />

          <Typography gutterBottom>{LangFile.PrescriptionAutoAdjustDialog.info4}</Typography>

          <Typography gutterBottom>{LangFile.PrescriptionAutoAdjustDialog.info5}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOnClose} color="primary" variant={'outlined'}>
            {LangFile.PrescriptionAutoAdjustDialog.close}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default withStyles(styles)(PrescriptionAutoAdjustDialog);

PrescriptionAutoAdjustDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onSetOpen: PropTypes.func.isRequired,
  isBulkPrescription: PropTypes.bool,
  appliedInitialIntervals: PropTypes.array,
  updateBulkJobsIntervals: PropTypes.func,
  bulkPrescriptionJobs: PropTypes.object,
};
